// Temporary direct CCSD-to-doctor lookup.
// Bypasses the speciality pipeline entirely — maps a CCSD code directly to
// its corresponding clinician(s) from the manual mapping.
// Next phase: derive this relationship from specialist↔speciality↔blog data.

import ccsdMapData from '@/data/ccsdCodeToDoctorMap.json';
import { specialityNameToSpecSlug } from '@/features/search/constants/specialityOptions';
import generatedDoctors from '@/generated/doctors.json';
import type { DoctorCard } from '@/types/doctor';

/**
 * Does a CCSD clinician name correspond to a doctor record?
 *
 * Names are the only reliable key: the `apiId` in the linked data is
 * misaligned with generated/doctors.json and resolves to other clinicians.
 * Surname must match exactly; every first-name token must match a token of the
 * doctor's name (equality or >=3-char prefix), which handles compound first
 * names ("Yu Zhen"), shortened records ("Rom" vs "Romualdas"), middle names
 * ("Amit" vs "Amit Suresh Patel") and punctuation ("Fitzgerald O’Connor" vs
 * "Fitzgerald-O'Connor"). Departed clinicians sharing only a surname (e.g.
 * "Mehra, Heena") are rejected.
 */
const normalizeNameToken = (value: string): string =>
  value.toLowerCase().replace(/[^a-z]/g, '');

const stripHonorific = (name: string): string =>
  name.replace(/^(dr|mr|mrs|ms|miss|prof|professor)\.?\s+/i, '');

export const clinicianMatchesDoctor = (
  clinician: string,
  doctorFullName: string,
): boolean => {
  const [lastPart = '', firstPart = ''] = clinician.split(',');
  const clinicianLast = normalizeNameToken(lastPart);
  const doctorTokens = stripHonorific(doctorFullName)
    .toLowerCase()
    .split(/\s+/)
    .map(normalizeNameToken)
    .filter(Boolean);
  const doctorLast = doctorTokens[doctorTokens.length - 1] ?? '';

  if (clinicianLast && doctorLast !== clinicianLast) return false;

  const clinicianFirstTokens = firstPart
    .split(/[\s-]+/)
    .map(normalizeNameToken)
    .filter(Boolean);
  if (clinicianFirstTokens.length === 0) return true;

  const tokenMatches = (a: string, b: string): boolean =>
    a === b ||
    (a.length >= 3 && b.startsWith(a)) ||
    (b.length >= 3 && a.startsWith(b));

  return clinicianFirstTokens.every((c) =>
    doctorTokens.some((d) => tokenMatches(c, d)),
  );
};

type CcsdCodeToDoctorMapData = {
  [code: string]: Array<{
    clinician: string;
    doctor: {
      slug: string;
      fullName: string;
      apiId: string;
    } | null;
  }>;
};

interface CcsdCodeToDoctorMap {
  data: CcsdCodeToDoctorMapData;
}

const ccsdMap = ccsdMapData as unknown as CcsdCodeToDoctorMap;

interface DirectCcsdDoctorMatch {
  code: string;
  clinician: string;
  doctor: {
    slug: string;
    fullName: string;
    apiId: string;
  };
}

/**
 * Direct lookup: returns the doctor(s) mapped to a CCSD code, bypassing the
 * speciality pipeline. Used as a temporary bridge until the specialist↔
 * speciality↔blog relationship is properly derived.
 */
export const resolveCcsdCodeToDoctorsDirect = (
  code: string,
): DirectCcsdDoctorMatch[] => {
  const entries = ccsdMap.data[code.toUpperCase()];
  if (!entries || entries.length === 0) return [];
  // Skip entries with no matching doctor record (departed clinicians) and
  // entries whose matched doctor name doesn't plausibly correspond to the
  // clinician (fuzzy-match false positives in the generated map).
  return entries
    .filter(
      (
        entry,
      ): entry is typeof entry & {
        doctor: NonNullable<typeof entry.doctor>;
      } => {
        if (!entry.doctor) return false;
        return clinicianMatchesDoctor(entry.clinician, entry.doctor.fullName);
      },
    )
    .map((entry) => ({
      code: code.toUpperCase(),
      clinician: entry.clinician,
      doctor: {
        slug: entry.doctor.slug,
        fullName: entry.doctor.fullName,
        apiId: entry.doctor.apiId,
      },
    }));
};

/**
 * Builds minimal DoctorCard entries from direct CCSD code matches.
 * Used to inject doctors into autocomplete results when the standard
 * speciality-based pipeline yields no results for a known CCSD code.
 */
export const buildDoctorCardsFromDirectCcsdMatch = (
  matches: DirectCcsdDoctorMatch[],
): DoctorCard[] => {
  // Index by slug for O(1) lookup of doctor data from generated/doctors.json.
  const bySlug = new Map<string, (typeof generatedDoctors)[number]>();
  for (const d of generatedDoctors) {
    if (d.slug) bySlug.set(d.slug, d);
  }

  const seen = new Set<string>();
  const cards: DoctorCard[] = [];

  for (const match of matches) {
    if (seen.has(match.doctor.slug)) continue;
    seen.add(match.doctor.slug);

    const doctorData = bySlug.get(match.doctor.slug);
    const specialities: string[] = [];
    let primarySpeciality: string | undefined;

    if (doctorData) {
      // Resolve primary speciality through the name→slug map. When no service
      // page exists for the speciality (e.g. Anaesthetics), fall back to the
      // raw speciality name so the result still shows a label.
      const primarySlug = specialityNameToSpecSlug(
        doctorData.primarySpeciality,
      );
      if (primarySlug) {
        primarySpeciality = primarySlug;
      } else if (doctorData.primarySpeciality) {
        primarySpeciality = doctorData.primarySpeciality;
      }

      // Resolve each doctor speciality to a SPEC_OPTION slug.
      const seenSpec = new Set<string>();
      for (const spec of doctorData.specialities ?? []) {
        const slug = specialityNameToSpecSlug(spec);
        if (slug && !seenSpec.has(slug)) {
          seenSpec.add(slug);
          if (slug !== primarySpeciality) specialities.push(slug);
        }
      }
    }

    cards.push({
      slug: match.doctor.slug,
      fullName: match.doctor.fullName,
      apiId: match.doctor.apiId,
      primarySpeciality,
      specialities,
      metaKeywords: [],
    });
  }

  return cards;
};

/**
 * Given a CCSD code query, returns DoctorCard entries from the direct
 * mapping. Returns empty array if the code is unknown or if the caller passes
 * ccsdEnabled=false (the feature flag gate in ccsdSearchGate).
 */
export const getDoctorsForCcsdCode = (
  code: string,
  ccsdEnabled: boolean,
): DoctorCard[] => {
  if (!ccsdEnabled) return [];
  const matches = resolveCcsdCodeToDoctorsDirect(code);
  if (matches.length === 0) return [];
  return buildDoctorCardsFromDirectCcsdMatch(matches);
};
